<template>
  <div class="video-box">
    <div id="dplayer" class="dplayer" ref="dplayer"></div>
    <!-- 时间节点标记 -->
    <div class="timeline-markers" v-if="timeStamps && timeStamps.length > 0" ref="timelineMarkers">
      <div 
        v-for="(timestamp, index) in timeStamps" 
        :key="index"
        class="timeline-marker"
        :style="{ left: getMarkerPosition(timestamp.t) + '%' }"
        @click="jumpToTimestamp(timestamp.t)"
        @mouseenter="showTooltip(timestamp, $event)"
        @mouseleave="scheduleHideTooltip"
      >
        <div class="marker-dot"></div>
      </div>
    </div>
    
    <!-- 节点信息提示框 -->
    <div 
      v-if="tooltipVisible && currentTooltip" 
      class="timestamp-tooltip"
      :style="tooltipStyle"
      @mouseenter="cancelHideTooltip"
      @mouseleave="scheduleHideTooltip"
    >
      <div class="tooltip-content">
        <div class="tooltip-title">{{ currentTooltip.tag || '时间节点' }}</div>
        <div class="tooltip-time">{{ currentTooltip.t }}</div>
        <div class="tooltip-image" v-if="currentTooltip.img">
          <img-load :src="setImages(currentTooltip.img)" alt="时间节点封面" />
          <!-- <img :src="setImages(currentTooltip.img)" alt="时间节点封面" /> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/api";
export default {
  name: 'VideoPlayer',
  props:{
    url: String,
    timeStamps: {
      type: Array,
      default: () => []
    }
  },
  data(){
    return {
      dPlayer: null,
      duration: 0,
      tooltipVisible: false,
      currentTooltip: null,
      tooltipStyle: {},
      hideTooltipTimer: null,
      initSeq: 0
    }
  },
  watch: {
    url(val, oldVal) {
      if (!val || val === oldVal) return;
      this.reloadPlayer();
    },
    timeStamps: {
      handler() {
        this.$nextTick(() => {
          
          this.adjustMarkersPosition();
        });
      },
      deep: true
    }
  },
  mounted() {
    this.init();
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.adjustMarkersPosition);
  },
  methods: {
    // 将时分秒格式转换为秒数
    timeToSeconds(timeStr) {
      if (!timeStr) return 0;
      console.log(timeStr, "-----");
      const parts = timeStr.toString().split(':');
      if (parts.length === 3) {
        // hh:mm:ss 格式
        const hours = parseInt(parts[0]) || 0;
        const minutes = parseInt(parts[1]) || 0;
        const seconds = parseInt(parts[2]) || 0;
        return hours * 3600 + minutes * 60 + seconds;
      } else if (parts.length === 2) {
        // mm:ss 格式
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseInt(parts[1]) || 0;
        return minutes * 60 + seconds;
      } else {
        // 纯数字，假设是秒数
        return parseInt(timeStr) || 0;
      }
    },

    // Chrome 141+ 会对 HLS 返回 canPlayType 真值，DPlayer 会改走原生 <video>；
    // 原生路径对 AES-128 加密 m3u8 不可靠，必须强制 hls.js（MSE）。
    resolveHls() {
      const mod = require('hls.js');
      return (mod && mod.default) ? mod.default : mod;
    },

    setupHls(video, player, source) {
      try {
        const Hls = this.resolveHls();
        if (!Hls || typeof Hls.isSupported !== 'function' || !Hls.isSupported()) {
          player.notice('Error: Hls is not supported.');
          return;
        }
        if (!source) {
          player.notice('Error: Empty video url.');
          return;
        }
        if (video.getAttribute('src')) {
          video.removeAttribute('src');
        }
        // 官方推荐顺序：先 loadSource 再 attachMedia。
        // 若先 attachMedia 再监听 MEDIA_ATTACHED，事件可能已同步触发，导致永远不 loadSource → 白屏。
        const hls = new Hls();
        player.plugins.hls = hls;
        hls.loadSource(source);
        hls.attachMedia(video);
        if (Hls.Events && Hls.Events.MANIFEST_PARSED) {
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          });
        }
        if (Hls.Events && Hls.Events.ERROR) {
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error('[XgPlayer][HLS]', data);
            if (data && data.fatal) {
              player.notice('Video load failed');
            }
          });
        }
        player.events.on('destroy', () => {
          try {
            hls.destroy();
          } catch (e) { /* ignore */ }
          delete player.plugins.hls;
        });
      } catch (err) {
        console.error('[XgPlayer] setupHls failed:', err);
        try {
          player.notice('Video load failed');
        } catch (e) { /* ignore */ }
      }
    },

    destroyPlayer() {
      if (this.dPlayer) {
        try {
          this.dPlayer.destroy();
        } catch (e) {
          console.warn('XgPlayer destroy failed:', e);
        }
        this.dPlayer = null;
      }
    },

    // 关闭弹窗 / 销毁前调用：作废异步 init 并销毁实例
    teardown() {
      this.initSeq += 1;
      this.destroyPlayer();
    },

    reloadPlayer() {
      this.teardown();
      this.$nextTick(() => {
        this.init();
      });
    },
    
    // 初始化播放器
    async init(){
      if (!this.url) {
        console.warn('XgPlayer: video url is empty, skip init');
        return;
      }
      if (!this.$refs.dplayer) {
        return;
      }
      const seq = ++this.initSeq;
      const playUrl = this.url;
      try {
        const dp = await import('dplayer');
        // url 变更 / 组件销毁后，丢弃过期的异步初始化
        if (seq !== this.initSeq || !this.$refs.dplayer) {
          return;
        }
        this.destroyPlayer();
        const _DPlayer = dp.default || dp;
        this.dPlayer = new _DPlayer({
          container: this.$refs.dplayer,
          // 等 HLS 清单解析后再播，避免空 src 时 play() 直接失败导致白屏
          autoplay: false,
          video: {
            // 故意不把 m3u8 写进 <video src>，否则 Chrome 会先走原生 HLS
            url: '',
            // 勿用 type:'hls'：DPlayer 在 canPlayType(mpegURL) 为真时会切到 native
            type: 'customHls',
            customType: {
              customHls: (video, player) => {
                this.setupHls(video, player, playUrl);
              }
            }
          },
        });

        // 监听播放时间变化
        this.dPlayer.on('timeupdate', () => {
          if (!this.dPlayer || !this.dPlayer.video) return;
          const currentTime = this.dPlayer.video.currentTime;
          const duration = this.dPlayer.video.duration;
          this.duration = duration;
          this.$emit('timeupdate', {
            currentTime: currentTime,
            duration: duration,
            formattedTime: this.formatTime(currentTime),
            formattedDuration: this.formatTime(duration)
          });
        });

        this.dPlayer.on('play', () => {
          if (!this.dPlayer || !this.dPlayer.video) return;
          this.$emit('triggerEvent', true);
          this.$emit('play', {
            currentTime: this.dPlayer.video.currentTime,
            duration: this.dPlayer.video.duration
          });
          setTimeout(() => {
            this.adjustMarkersPosition();
          }, 100);
        });

        this.dPlayer.on('pause', () => {
          if (!this.dPlayer || !this.dPlayer.video) return;
          this.$emit('triggerEvent', false);
          this.$emit('pause', {
            currentTime: this.dPlayer.video.currentTime,
            duration: this.dPlayer.video.duration
          });
        });

        this.dPlayer.on('loadedmetadata', () => {
          setTimeout(() => {
            this.adjustMarkersPosition();
          }, 500);
        });

        this.dPlayer.on('canplay', () => {
          setTimeout(() => {
            this.adjustMarkersPosition();
          }, 300);
        });
      } catch (err) {
        console.error('[XgPlayer] init failed:', err);
      }
    },
    
    // 计算时间节点在进度条上的位置
    getMarkerPosition(timestamp) {
      if (!this.duration || this.duration === 0) return 0;
      const timestampSeconds = this.timeToSeconds(timestamp);
      return (timestampSeconds / this.duration) * 100;
    },
    
    // 跳转到指定时间戳
    jumpToTimestamp(timestamp) {
      if (this.dPlayer && this.dPlayer.video) {
        const timestampSeconds = this.timeToSeconds(timestamp);
        this.dPlayer.seek(timestampSeconds);
        this.hideTooltip();
      }
    },
    
    // 显示节点信息提示框
    showTooltip(timestamp, event) {
      // 清除可能存在的隐藏定时器
      this.cancelHideTooltip();
      
      this.currentTooltip = timestamp;
      this.tooltipVisible = true;
      
      // 计算提示框位置
      const rect = event.target.getBoundingClientRect();
      const containerRect = this.$refs.dplayer.getBoundingClientRect();
      
      // 根据内容计算提示框高度
      const estimatedHeight = timestamp.img ? 220 : 100; // 有图片时更高
      
      // 改进位置计算，确保提示框显示在滚动条上方
      const leftPos = rect.left - containerRect.left - 100;
      // 确保提示框在控制栏上方显示，距离底部足够远
      const topPos = rect.top - containerRect.top - estimatedHeight - 30; // 30px额外间距
      
      this.tooltipStyle = {
        left: Math.max(10, Math.min(leftPos, containerRect.width - 230)) + 'px', // 确保不超出边界
        top: Math.max(15, topPos) + 'px' // 确保不超出顶部，留出更多空间
      };
    },
    
    // 计划隐藏提示框（延迟执行）
    scheduleHideTooltip() {
      this.hideTooltipTimer = setTimeout(() => {
        this.hideTooltip();
      }, 200); // 200ms延迟，给用户时间将鼠标移到提示框上
    },
    
    // 取消隐藏提示框
    cancelHideTooltip() {
      if (this.hideTooltipTimer) {
        clearTimeout(this.hideTooltipTimer);
        this.hideTooltipTimer = null;
      }
    },
    
    // 隐藏提示框
    hideTooltip() {
      this.tooltipVisible = false;
      this.currentTooltip = null;
      this.cancelHideTooltip();
    },
    
    // 格式化时间为 hh:mm:ss 格式
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00:00';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    // 获取当前时间戳的视频截图（支持上传）
    async captureScreenshot(options = {}) {
      try {
        if (!this.dPlayer || !this.dPlayer.video) {
          console.error('播放器未初始化');
          return null;
        }
        
        const video = this.dPlayer.video;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 设置canvas尺寸为视频尺寸
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // 将当前视频帧绘制到canvas上
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // 获取当前时间戳
        const currentTime = video.currentTime;
        const formattedTime = this.formatTime(currentTime);
        
        // 设置默认选项
        const config = {
          quality: 0.9,
          format: 'image/jpeg',
          filename: `video-screenshot-${formattedTime.replace(':', '-')}.jpg`,
          returnType: 'all', // 'base64', 'blob', 'file', 'all'
          ...options
        };
        
        // 创建基础截图数据
        const screenshotData = {
          currentTime: currentTime,
          formattedTime: formattedTime,
          timestamp: new Date().getTime(),
          videoWidth: canvas.width,
          videoHeight: canvas.height
        };
        
        // 检查Canvas是否被污染
        let isCanvasTainted = false;
        
        try {
          // 尝试访问canvas数据来检测是否被污染
          canvas.getImageData(0, 0, 1, 1);
        } catch (error) {
          if (error.name === 'SecurityError') {
            isCanvasTainted = true;
            console.warn('Canvas被污染，无法导出图片。这通常是由于跨域视频源导致的。');
          }
        }
        
        if (isCanvasTainted) {
          // Canvas被污染时的降级方案
          const errorMsg = '由于跨域限制，无法生成视频截图。请确保视频源支持CORS或使用同域视频源。';
          console.error(errorMsg);
          
          // 触发错误事件
          this.$emit('screenshot-error', {
            message: errorMsg,
            type: 'CORS_ERROR',
            isCanvasTainted: true
          });
          
          // 返回基础信息，但不包含图片数据
          return {
            ...screenshotData,
            error: errorMsg,
            isCanvasTainted: true
          };
        }
        
        // 根据返回类型生成不同格式的数据
        if (config.returnType === 'base64' || config.returnType === 'all') {
          try {
            screenshotData.imageData = canvas.toDataURL(config.format, config.quality);
          } catch (error) {
            console.error('生成base64数据失败:', error);
            screenshotData.imageDataError = error.message;
          }
        }
        
        if (config.returnType === 'blob' || config.returnType === 'all') {
          try {
            screenshotData.blob = await new Promise((resolve, reject) => {
              canvas.toBlob((blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('无法生成blob数据'));
                }
              }, config.format, config.quality);
            });
          } catch (error) {
            console.error('生成blob数据失败:', error);
            screenshotData.blobError = error.message;
          }
        }
        
        if (config.returnType === 'file' || config.returnType === 'all') {
          try {
            const blob = await new Promise((resolve, reject) => {
              canvas.toBlob((blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('无法生成blob数据'));
                }
              }, config.format, config.quality);
            });
            screenshotData.file = new File([blob], config.filename, { 
              type: config.format,
              lastModified: Date.now()
            });
          } catch (error) {
            console.error('生成文件失败:', error);
            screenshotData.fileError = error.message;
          }
        }
        
        // 触发截图事件，传递截图数据给父组件
        this.$emit('screenshot', screenshotData);
        
        return screenshotData;
      } catch (error) {
        console.error('截图失败:', error);
        this.$emit('screenshot-error', error);
        return null;
      }
    },
    
    // 获取截图并上传封面
    async captureAndUploadCover(options = {}) {
      try {
        // 获取截图（返回File对象）
        const screenshotData = await this.captureScreenshot({
          returnType: 'file',
          format: 'image/jpeg',
          quality: 0.9,
          ...options
        });
        
        if (!screenshotData || !screenshotData.file) {
          if (screenshotData && screenshotData.isCanvasTainted) {
            throw new Error('由于跨域限制，无法生成视频截图。请确保视频源支持CORS或使用同域视频源。');
          }
          throw new Error('截图失败');
        }
        
        const formData = new FormData();
        formData.append('upload', screenshotData.file);

        const { data } = await API.fileUpload(formData);
        console.log(data, 'data');
        // 触发上传成功事件
        this.$emit('cover-uploaded', {
          screenshot: screenshotData,
          uploadResult: data.coverImg
        });
        
        return {
          screenshot: screenshotData,
          uploadResult: data.coverImg
        };
      } catch (error) {
        console.error('截图上传失败:', error);
        this.$emit('cover-upload-error', error);
        throw error;
      }
    },
    
    // 测试Canvas是否被污染的方法
    testCanvasTaint() {
      try {
        if (!this.dPlayer || !this.dPlayer.video) {
          console.log('播放器未初始化');
          return false;
        }
        
        const video = this.dPlayer.video;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // 尝试访问canvas数据
        canvas.getImageData(0, 0, 1, 1);
        console.log('Canvas未被污染，可以正常截图');
        return false;
      } catch (error) {
        if (error.name === 'SecurityError') {
          console.log('Canvas被污染，无法截图:', error.message);
          return true;
        }
        console.log('其他错误:', error.message);
        return false;
      }
    },
    
    setImages(val) {
        // 判断是否拥有域名 如果有 去掉域名 字符串第一位是'/'
        if (val.indexOf("http") === 0) {
          val = "/" + val.split("/").slice(3).join("/");
        } else if (val.indexOf("/") !== 0) {
          val = "/" + val;
        }
        // 根据去掉域名后的数据进行匹配
        let data = JSON.parse(JSON.parse(localStorage.getItem("imageUrl")));
        let strValue = "";
        for (const key in data) {
          if (key.indexOf("/") !== 0 && key !== "*") {
            //如果匹配键名前面不带'/'
            if (val.indexOf("/" + key + "/") === 0) {
              //
              strValue = data[key];
            }
          } else if (key.indexOf("/") === 0 && key !== "*") {
            //带'/'
            if (val.indexOf(key + "/") === 0) {
              //有匹配的
              strValue = data[key];
            }
          }
        }
        if (strValue) {
          return strValue + val;
        } else {
          return data["*"] + val;
        }
      },
    // 动态调整时间节点位置以对齐进度条
    adjustMarkersPosition() {
      this.$nextTick(() => {
        const container = this.$refs.dplayer;
        const markersContainer = this.$refs.timelineMarkers;
        
        if (!container || !markersContainer) return;
        
        // 查找DPlayer的进度条元素
        const progressSelectors = [
          '.dplayer-bar-wrap',
          '.dplayer-controller-bar', 
          '.dplayer-bar',
          '.dplayer-controller .dplayer-bar-wrap'
        ];
        
        let progressBar = null;
        for (const selector of progressSelectors) {
          progressBar = container.querySelector(selector);
          if (progressBar) break;
        }
        
        if (progressBar) {
          const containerRect = container.getBoundingClientRect();
          const progressRect = progressBar.getBoundingClientRect();
          
          // 计算让节点直接在进度条上的位置
          const bottomDistance = containerRect.bottom - progressRect.bottom; // 从底部到进度条底部的距离
          const leftDistance = progressRect.left - containerRect.left;
          const rightDistance = containerRect.right - progressRect.right;
          
          // 动态设置位置 - 让节点直接在进度条上
          markersContainer.style.bottom = `${bottomDistance}px`; // 直接对齐进度条底部
          markersContainer.style.left = `${Math.max(leftDistance, 0)}px`;
          markersContainer.style.right = `${Math.max(rightDistance, 0)}px`;
          markersContainer.style.height = `${progressRect.height}px`; // 使用进度条的实际高度
          
          console.log('时间节点位置已调整到进度条上:', {
            bottom: `${bottomDistance}px`,
            left: `${leftDistance}px`, 
            right: `${rightDistance}px`,
            height: `${progressRect.height}px`,
            progressRect: progressRect
          });
        } else {
          console.warn('未找到DPlayer进度条，使用默认定位');
        }
      });
    }
  },
  beforeDestroy() {
    this.cancelHideTooltip();
    window.removeEventListener('resize', this.adjustMarkersPosition);
    this.teardown();
  }
}
</script>

<style lang="scss" scoped>
.video-box {
  height: 100%;
  width: 100%;
  position: relative;
  
  .dplayer {
    height: 100%;
    width: 100%;
    border-radius: 4px;
  }
  
  // 时间节点标记容器
  .timeline-markers {
    position: absolute;
    bottom: 46px; // 默认值，会被JS动态覆盖
    left: 50px; // 默认值，会被JS动态覆盖
    right: 80px; // 默认值，会被JS动态覆盖
    height: 4px; // 默认值，会被JS动态覆盖
    pointer-events: none;
    z-index: 999 !important;
  }
  
  // 单个时间节点标记
  .timeline-marker {
    position: absolute;
    top: 50%; // 使用百分比垂直居中
    transform: translateY(-50%); // 精确居中对齐
    width: 8px;
    height: 8px;
    margin-left: -4px;
    pointer-events: all;
    cursor: pointer;
    z-index: 1000 !important;
    
    .marker-dot {
      width: 100%;
      height: 100%;
      background: #ff4757;
      border-radius: 50%;
      border: 1px solid #ffffff;
      box-shadow: 0 0 0 1px rgba(255, 71, 87, 0.6);
      transition: all 0.3s ease;
      
      &:hover {
        transform: scale(1.8);
        background: #ff3838;
        box-shadow: 0 0 0 2px rgba(255, 71, 87, 0.8);
        border-color: #ffda79;
      }
    }
  }
  
  // 节点信息提示框
  .timestamp-tooltip {
    position: absolute;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 0;
    border-radius: 8px;
    font-size: 12px;
    z-index: 1001; // 提高层级，确保在最上层
    min-width: 200px;
    max-width: 250px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    pointer-events: all; // 确保可以接收鼠标事件
    opacity: 1;
    transition: opacity 0.2s ease;
    
    .tooltip-content {
      padding: 12px;
      
      .tooltip-title {
        font-weight: bold;
        margin-bottom: 4px;
        color: #ffffff;
        font-size: 14px;
        word-break: break-word;
      }
      
      .tooltip-time {
        color: #409eff;
        margin-bottom: 8px;
        font-size: 12px;
        font-weight: 500;
        background: rgba(64, 158, 255, 0.2);
        padding: 2px 8px;
        border-radius: 12px;
        display: inline-block;
      }
      
      .tooltip-image {
        height: 100px;
        .img-wrap {
          width: 100%;
          height: 100%;
        }
      }
    }
    
    // 添加箭头效果
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: rgba(0, 0, 0, 0.9) transparent transparent transparent;
    }
  }
}

// 确保时间节点在DPlayer进度条上正确显示
:deep(.dplayer-controller) {
  z-index: 10 !important;
}

:deep(.dplayer-controller-bar) {
  z-index: 11 !important;
}

// 确保进度条区域的层级
:deep(.dplayer-bar-wrap) {
  z-index: 12 !important;
}

// DPlayer进度条相关样式
:deep(.dplayer-bar) {
  z-index: 13 !important;
}

// 确保播放按钮等控件的层级
:deep(.dplayer-controller .dplayer-icons) {
  z-index: 14 !important;
}
</style>
